/**
 * custom.js - UI Optimizations & Performance Enhancements
 *
 * PURPOSE:
 * Site-specific UI functionality, performance optimizations, and third-party
 * scripts that need DOM/cookie/top-window access (so cannot run from Shopify
 * Customer Events pixels, which run in a sandboxed iframe without
 * allow-same-origin).
 *
 * THIS FILE HANDLES:
 * - Swiper.js lazy loading (UI component)
 * - Trustpilot TrustBox widget (UI component)
 * - DNS preconnect optimization
 * - Duplicate script prevention
 *
 * MODIFICATION GUIDELINES:
 * - Test Lighthouse scores after changes (target: TBT < 300ms)
 * - Maintain the existing lazy-loading patterns (requestIdleCallback + fallbacks)
 * - See CLAUDE.md for full development guidelines
 */

// Performance Optimizations
(function () {
  "use strict";

  // Track what's been loaded to prevent duplicates
  window._lazyLoadedScripts = window._lazyLoadedScripts || new Set();

  /**
   * Lazy load a script after user interaction or page idle
   * @param {string} src - Script source URL
   * @param {Object} options - Script attributes
   * @param {number} delay - Delay in ms (default: 0)
   * @returns {Promise} - Resolves when script is loaded
   */
  function lazyLoadScript(src, options = {}, delay = 0) {
    // Normalize Swiper URLs to prevent version conflicts
    const normalizedSrc = src.includes('swiper')
      ? 'https://cdn.jsdelivr.net/npm/swiper@9/swiper-bundle.min.js'
      : src;

    return new Promise((resolve, reject) => {
      const loadScript = () => {
        // Check if script already exists or is being loaded
        if (window._lazyLoadedScripts.has(normalizedSrc)) {
          resolve();
          return;
        }

        // For Swiper, check if library is already loaded
        if (normalizedSrc.includes('swiper') && typeof window.Swiper !== 'undefined') {
          window._lazyLoadedScripts.add(normalizedSrc);
          resolve();
          return;
        }

        // Check if script tag already exists
        const existingScript = document.querySelector(`script[src*="${normalizedSrc.split('/').pop().split('?')[0]}"]`);
        if (existingScript) {
          window._lazyLoadedScripts.add(normalizedSrc);
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = normalizedSrc;

        // Apply any additional attributes
        Object.keys(options).forEach(key => {
          script.setAttribute(key, options[key]);
        });

        script.onload = () => {
          window._lazyLoadedScripts.add(normalizedSrc);
          resolve();
        };
        script.onerror = () => reject(new Error(`Failed to load script: ${normalizedSrc}`));

        document.body.appendChild(script);
      };

      if (delay > 0) {
        setTimeout(loadScript, delay);
      } else {
        loadScript();
      }
    });
  }

  /**
   * Load Swiper CSS and JS when needed
   */
  function loadSwiper() {
    return new Promise((resolve) => {
      // Check if already loaded
      if (typeof window.Swiper !== 'undefined') {
        resolve();
        return;
      }

      // Check if CSS is already loaded
      const existingCSS = document.querySelector('link[href*="swiper"]');
      if (!existingCSS) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://cdn.jsdelivr.net/npm/swiper@9/swiper-bundle.min.css';
        document.head.appendChild(link);
      }

      // Load JS
      lazyLoadScript('https://cdn.jsdelivr.net/npm/swiper@9/swiper-bundle.min.js')
        .then(resolve)
        .catch(() => resolve()); // Continue even if load fails
    });
  }

  /**
   * Wait for user interaction or visibility before loading Swiper
   */
  function initSwiperLazyLoad() {
    // Check if any Swiper sections exist on the page
    const swiperSections = document.querySelectorAll('[class*="ss-slideshow"], [class*="ss-testimonial"], [class*="ss-slider"], [class*="ss-feature"], [class*="ss-comparison"], .swiper, [data-swiper]');

    if (swiperSections.length === 0) {
      return; // No Swiper needed on this page
    }

    let loaded = false;

    const load = () => {
      if (loaded) return;
      loaded = true;
      loadSwiper();
    };

    // Use Intersection Observer for better performance
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            load();
            observer.disconnect();
          }
        });
      }, {
        rootMargin: '50px' // Start loading 50px before section is visible
      });

      swiperSections.forEach(section => observer.observe(section));
    } else {
      // Fallback: load on scroll or after 2 seconds
      const scrollHandler = () => {
        load();
        window.removeEventListener('scroll', scrollHandler);
      };
      window.addEventListener('scroll', scrollHandler, { passive: true });
      setTimeout(load, 2000);
    }
  }

  /**
   * Load scripts when browser is idle
   */
  function loadOnIdle() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        loadUIWidgets();
      }, { timeout: 3000 });
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(loadUIWidgets, 3000);
    }
  }

  /**
   * Load UI widgets and third-party scripts that need top-window access
   */
  function loadUIWidgets() {
    // TrustBox (Trustpilot widget - needs DOM for rendering)
    lazyLoadScript('https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js',
      { async: true },
      2000
    );
  }

  /**
   * Preconnect to critical third-party domains
   * This helps reduce DNS lookup time for essential resources
   */
  function addPreconnects() {
    const domains = [
      { href: 'https://cdn.jsdelivr.net', crossOrigin: true }, // Swiper
      { href: 'https://widget.trustpilot.com', crossOrigin: true }
    ];

    domains.forEach(({ href, crossOrigin }) => {
      // Don't add duplicate preconnects
      if (document.querySelector(`link[rel="preconnect"][href="${href}"]`)) {
        return;
      }

      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      if (crossOrigin) {
        link.crossOrigin = 'anonymous';
      }
      document.head.appendChild(link);
    });
  }

  /**
   * Override Swiper script loads from sections
   * Prevents duplicate loads from legacy section code
   */
  function preventDuplicateSwiperLoads() {
    // Create a MutationObserver to catch dynamically added Swiper scripts
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.tagName === 'SCRIPT' && node.src && node.src.includes('swiper')) {
            // If Swiper is already loaded or loading, remove the duplicate
            if (window._lazyLoadedScripts.has('swiper') || typeof window.Swiper !== 'undefined') {
              node.remove();
            }
          }
        });
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  // Initialize optimizations based on document ready state
  function initialize() {
    addPreconnects();
    initSwiperLazyLoad();
    loadOnIdle();
    preventDuplicateSwiperLoads();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  // Expose Swiper loader for sections that need it immediately
  window.loadSwiperIfNeeded = loadSwiper;

})();

/**
 * Fix null element access in renderFilterValues
 *
 * The theme's loess-product-filters iterates current DOM filter buttons and
 * looks up matching toggle/panel elements in the AJAX response HTML. When a
 * filter group disappears between requests (e.g. no matching options left),
 * querySelector returns null and toggle.setAttribute crashes.
 *
 * Monkey-patches the prototype method to add null guards.
 */
(function() {
  'use strict';

  const FilterClass = customElements.get('loess-product-filters');
  if (!FilterClass) return;

  FilterClass.prototype.renderFilterValues = function(html) {
    const filterDrawersParent = html.getElementById('FilterDrawers');
    if (!filterDrawersParent) return;

    // --- Mobile filters ---
    const filterMobileTemp = filterDrawersParent.querySelector('#FilterDrawerMobile');
    if (filterMobileTemp) {
      const filterMobileButtons = Array.from(this.querySelectorAll('.scroller-tabs button'));
      const expandedBtn = this.querySelector('.scroller-tabs button[aria-expanded="true"]');
      const filterMobileOffsetLeft = expandedBtn ? expandedBtn.offsetLeft : 0;
      const drawerContent = this.querySelector('.drawer__content');
      const filterMobileScrollTop = drawerContent ? drawerContent.scrollTop : 0;

      filterMobileButtons.forEach(button => {
        const ariaControls = button.getAttribute('aria-controls');
        const toggle = filterMobileTemp.querySelector(`[aria-controls="${ariaControls}"]`);
        const panel = filterMobileTemp.querySelector(`[id="${ariaControls}"]`);
        if (!toggle || !panel) return;

        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        if (toggle.parentElement) toggle.parentElement.toggleAttribute('active', isExpanded);
        panel.removeAttribute('hidden');
        panel.removeAttribute('open');
        panel.setAttribute(isExpanded ? 'open' : 'hidden', '');
      });

      const mobileDest = this.querySelector('#FilterDrawerMobile');
      const mobileSrc = filterDrawersParent.querySelector('#FilterDrawerMobile');
      if (mobileDest && mobileSrc) mobileDest.innerHTML = mobileSrc.innerHTML;

      const scrollerTabs = this.querySelector('loess-scroller-tabs');
      if (scrollerTabs && scrollerTabs.selectedButton) {
        scrollerTabs.scrollTo({
          behavior: 'instant',
          left: filterMobileOffsetLeft - scrollerTabs.clientWidth / 2 + scrollerTabs.selectedButton.clientWidth / 2
        });
      }
      if (drawerContent) {
        drawerContent.scrollTop = filterMobileScrollTop;
      }
    }

    // --- Desktop/large filters ---
    const filterLargeTemp = filterDrawersParent.querySelector('#FilterDrawerLarge');
    if (filterLargeTemp) {
      const filterLargeButtons = Array.from(this.querySelectorAll('.collection-filter > button[is="loess-button"]'));
      filterLargeButtons.forEach(button => {
        const ariaControls = button.getAttribute('aria-controls');
        const toggle = filterLargeTemp.querySelector(`[aria-controls="${ariaControls}"]`);
        if (!toggle) return;

        const isExpanded = button.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
        if (toggle.nextElementSibling) {
          toggle.nextElementSibling.toggleAttribute('open', isExpanded);
        }

        const listToggleButton = button.nextElementSibling
          ? button.nextElementSibling.querySelector('.collection-filter__more-button')
          : null;
        if (listToggleButton && listToggleButton.getAttribute('aria-expanded') == 'true') {
          const toggleButtonIndex = listToggleButton.getAttribute('index');
          const toggleButton = filterLargeTemp.querySelector(`.collection-filter__more-button[index="${toggleButtonIndex}"]`);
          if (toggleButton && toggleButton.previousElementSibling) {
            const hiddenItems = Array.from(toggleButton.previousElementSibling.querySelectorAll('.collection-filter__list-item--hidden'));
            hiddenItems.forEach(element => {
              element.removeAttribute('hidden');
              toggleButton.setAttribute('aria-expanded', 'true');
            });
          }
        }
      });
      const largeDest = this.querySelector('#FilterDrawerLarge');
      if (largeDest) largeDest.innerHTML = filterLargeTemp.innerHTML;
    }
  };
})();

/**
 * Filter checkbox sync for merged case-insensitive values
 *
 * When filter values are merged (e.g., "CUBE", "Cube", "cube" → "Cube"),
 * clicking the visible checkbox needs to sync all hidden variant checkboxes.
 *
 * Uses event delegation on stable container elements to avoid issues with
 * listeners being lost or duplicated when the theme replaces filter DOM via
 * innerHTML after each AJAX filter update.
 */
(function() {
  'use strict';

  function handleFilterGroupChange(event) {
    const visibleCheckbox = event.target;
    if (visibleCheckbox.type !== 'checkbox' || !visibleCheckbox.dataset.filterGroup) return;

    const groupId = visibleCheckbox.dataset.filterGroup;
    const form = visibleCheckbox.closest('form');
    if (!form) return;

    // Sync visible → hidden checkboxes within the same form
    const hiddenCheckboxes = form.querySelectorAll(
      `input[type="checkbox"][data-filter-group-member="${groupId}"]`
    );
    if (hiddenCheckboxes.length === 0) return;

    hiddenCheckboxes.forEach(hidden => {
      hidden.checked = visibleCheckbox.checked;
    });

    // Dispatch filters:changed with form data as search params
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData).toString();
    form.dispatchEvent(new CustomEvent('filters:changed', {
      bubbles: true,
      detail: { searchParams }
    }));
  }

  // Sync visible checkbox state from hidden checkboxes (e.g. after AJAX update)
  function syncVisibleCheckboxes(container) {
    container.querySelectorAll('[data-filter-group]').forEach(visibleCheckbox => {
      const groupId = visibleCheckbox.dataset.filterGroup;
      const form = visibleCheckbox.closest('form');
      if (!form) return;

      const anyChecked = form.querySelectorAll(
        `input[type="checkbox"][data-filter-group-member="${groupId}"]`
      );
      visibleCheckbox.checked = Array.from(anyChecked).some(cb => cb.checked);
    });
  }

  function init() {
    // Attach delegated change listeners to the stable drawer containers.
    // These survive innerHTML replacements because the containers themselves persist.
    const containers = document.querySelectorAll('#FilterDrawerLarge, #FilterDrawerMobile');
    containers.forEach(container => {
      // Guard against double-attaching if init runs more than once
      if (container.dataset.filterSyncBound) return;
      container.dataset.filterSyncBound = '1';
      container.addEventListener('change', handleFilterGroupChange);
    });

    // Sync visible checkbox state on page load and after AJAX updates
    containers.forEach(syncVisibleCheckboxes);
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-sync after AJAX filter updates (theme replaces drawer innerHTML)
  const filterObserver = new MutationObserver(() => {
    // Just re-sync visible checkbox states; delegated listeners are still active
    document.querySelectorAll('#FilterDrawerLarge, #FilterDrawerMobile').forEach(syncVisibleCheckboxes);
  });

  function startObserving() {
    document.querySelectorAll('#FilterDrawerLarge, #FilterDrawerMobile').forEach(container => {
      filterObserver.observe(container, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserving);
  } else {
    startObserving();
  }
})();

/**
 * Show Sold toggle for brand/model collection pages
 * 
 * Toggles visibility of sold items in the product grid.
 * Sold items are rendered with class "product-grid__item--sold" and hidden attribute.
 */
(function() {
  'use strict';

  function initShowSoldToggle() {
    const toggles = document.querySelectorAll('[data-show-sold-toggle]');
    
    toggles.forEach(toggle => {
      // Sync all toggles (mobile + desktop) and update grid
      toggle.addEventListener('change', () => {
        const isChecked = toggle.checked;
        
        // Sync all show-sold toggles
        document.querySelectorAll('[data-show-sold-toggle]').forEach(t => {
          t.checked = isChecked;
        });
        
        // Toggle visibility of sold items
        const soldItems = document.querySelectorAll('.product-grid__item--sold');
        soldItems.forEach(item => {
          if (isChecked) {
            item.removeAttribute('hidden');
          } else {
            item.setAttribute('hidden', '');
          }
        });
      });
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initShowSoldToggle);
  } else {
    initShowSoldToggle();
  }

  // Re-init after AJAX filter updates (product grid gets replaced)
  const gridObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.addedNodes.length > 0) {
        const hasProductGrid = Array.from(mutation.addedNodes).some(node => 
          node.nodeType === 1 && (
            node.querySelector?.('.product-grid__item--sold') ||
            node.classList?.contains('product-grid')
          )
        );
        if (hasProductGrid) {
          // Re-apply show sold state after grid update
          const toggle = document.querySelector('[data-show-sold-toggle]');
          if (toggle && toggle.checked) {
            document.querySelectorAll('.product-grid__item--sold').forEach(item => {
              item.removeAttribute('hidden');
            });
          }
          break;
        }
      }
    }
  });

  function startGridObserving() {
    const gridContainer = document.querySelector('#FilterProductGrid');
    if (gridContainer) {
      gridObserver.observe(gridContainer, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startGridObserving);
  } else {
    startGridObserving();
  }
})();

/**
 * Null-guard patches for theme.js
 *
 * theme.js is the core Upscale theme bundle and cannot be edited directly.
 * Several querySelector calls crash when elements are absent from the DOM
 * (e.g. cart errors span, predictive search results, variant data, payment
 * installments banner). These patches override the affected prototype methods
 * with null-safe versions.
 */
(function () {
  'use strict';

  // --- CartItems: updateQuantity catch handler (theme.js:253-254) ---
  var CartItemsClass = customElements.get('loess-cart-items');
  if (CartItemsClass) {
    CartItemsClass.prototype.updateQuantity = function (line, quantity, name) {
      var self = this;
      var _ce;
      self.enableLoading(line);
      _ce = document.querySelector('.cart-errors');
      if (_ce) _ce.classList.remove('cart-errors--visible');
      var body = JSON.stringify({
        line: line,
        quantity: quantity,
        sections: self.getSectionsToRender().map(function (s) { return s.section; }),
        sections_url: window.location.pathname
      });
      fetch(window.LoessTheme.routes.cart_change_url, Object.assign({}, fetchConfig(), { body: body }))
        .then(function (r) { return r.json(); })
        .then(function (state) {
          self.renderCartItems(state);
          self.disableLoading();
        })
        .catch(function () {
          self.querySelectorAll('.loading-overlay').forEach(function (o) { o.classList.add('hidden'); });
          var ce = document.querySelector('.cart-errors');
          if (ce) ce.classList.add('cart-errors--visible');
          var span = document.querySelector('.cart-errors > span');
          if (span) span.textContent = window.LoessTheme.cartStrings.error;
          self.disableLoading();
        });
    };
  }

  // --- Predictive search: onChange (theme.js:2726-2736) ---
  var PredictiveSearchClass = customElements.get('loess-predictive-search');
  if (PredictiveSearchClass) {
    PredictiveSearchClass.prototype.onChange = function () {
      var searchTerm = this.getQuery();
      if (this.results) this.results.textContent = '';
      var resultsEl = this.querySelector('loess-predictive-search-results');
      if (resultsEl) {
        resultsEl.hidden = false;
        resultsEl.setAttribute('loading', '');
      }
      if (!searchTerm.length) {
        if (resultsEl) resultsEl.hidden = true;
        return;
      }
      this.searchTerm = searchTerm;
      this.getSearchResults();
    };
  }

  // --- Variant picker: getVariantData (theme.js:3204-3206) ---
  var VariantPickerClass = customElements.get('loess-variant-picker');
  if (VariantPickerClass) {
    VariantPickerClass.prototype.getVariantData = function () {
      if (!this.variantData) {
        var el = document.querySelector('#ProductVariantData-' + this.sectionId + '[type="application/json"]');
        this.variantData = el ? JSON.parse(el.textContent) : [];
      }
      return this.variantData;
    };

    // --- Variant picker: checkForInstallmentsBanner (theme.js:3241-3248) ---
    VariantPickerClass.prototype.checkForInstallmentsBanner = async function () {
      var modalProduct = this.closest('loess-modal-product');
      if (!modalProduct) return;
      await new Promise(function (r) { return setTimeout(r, 500); });
      var paymentTerms = modalProduct.querySelector('shopify-payment-terms');
      if (!paymentTerms || !paymentTerms.shadowRoot) return;
      var installments = paymentTerms.shadowRoot.querySelector('.shopify-installments');
      if (installments) installments.style.pointerEvents = 'none';
      var learnMore = paymentTerms.shadowRoot.querySelector('.shopify-installments__learn-more');
      if (learnMore) learnMore.style.pointerEvents = 'auto';
    };
  }
})();

/**
 * Null-guard: renderProductGrid & renderPage empty-results branch (theme.js:2552-2586)
 *
 * renderProductGrid crashes when:
 * - document.querySelector('.collection-bar') is null (no parentElement)
 * - document.getElementById('FilterProductGrid') is null
 * - html.getElementById('FilterProductGrid') is null
 *
 * renderPage empty-results branch crashes when FilterProductGrid is absent.
 * renderProductCount crashes when FilterProductCount is absent.
 *
 * Note: innerHTML assignments use trusted same-origin Shopify section HTML
 * fetched by the theme's own renderPage method — not user-supplied content.
 */
(function () {
  'use strict';

  var FilterClass = customElements.get('loess-product-filters');
  if (!FilterClass) return;

  FilterClass.prototype.renderProductGrid = function (html) {
    var collectionBar = document.querySelector('.collection-bar');
    var htmlCollectionBar = html.querySelector('.collection-bar');
    if (collectionBar && collectionBar.parentElement && htmlCollectionBar && htmlCollectionBar.parentElement) {
      collectionBar.parentElement.innerHTML = htmlCollectionBar.parentElement.innerHTML; // safe: trusted Shopify section HTML
    }
    var grid = document.getElementById('FilterProductGrid');
    var htmlGrid = html.getElementById('FilterProductGrid');
    if (grid && htmlGrid) {
      grid.innerHTML = htmlGrid.innerHTML; // safe: trusted Shopify section HTML
    }
  };

  FilterClass.prototype.renderProductCount = function (html) {
    var count = document.getElementById('FilterProductCount');
    var htmlCount = html.getElementById('FilterProductCount');
    if (count && htmlCount) {
      count.innerHTML = htmlCount.innerHTML; // safe: trusted Shopify section HTML
    }
  };

  FilterClass.prototype.renderPage = async function (event, updateURLHash) {
    if (updateURLHash === undefined) updateURLHash = true;
    var _event$detail;
    var searchParams = event === null || event === void 0 ? void 0 : (_event$detail = event.detail) === null || _event$detail === void 0 ? void 0 : _event$detail.searchParams;
    if (!updateURLHash) searchParams = event;
    this.searchParamsPrev = searchParams;
    var url = window.location.pathname + '?section_id=' + this.sectionId + '&' + (this.terms ? 'q=' + this.terms : '') + '&' + searchParams;
    this.setAttribute('loading', '');
    var response = await fetch(url);
    var responseText = await response.text();
    var html = new DOMParser().parseFromString(responseText, 'text/html');
    if (!html.querySelector('.product-grid-empty')) {
      this.renderProductGrid(html);
      this.renderFilterValues(html, event);
      this.renderActiveFilters(html);
      this.renderProductCount(html);
      if (updateURLHash) this.updateURLHash(searchParams);
    } else {
      var countText = document.querySelector('.collection-utility-bar__count-text');
      if (countText) countText.style.display = 'none';
      var sorting = document.querySelector('.collection-utility-bar__sorting');
      if (sorting) sorting.style.display = 'none';
      var grid = document.getElementById('FilterProductGrid');
      if (grid) {
        grid.innerHTML = ''; // safe: clearing element
        var emptyEl = html.querySelector('.product-grid-empty');
        if (emptyEl) grid.insertAdjacentElement('afterbegin', emptyEl);
      }
    }
    this.removeAttribute('loading');
    var self = this;
    requestAnimationFrame(function () {
      var collection = self.querySelector('.collection');
      if (collection) {
        collection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };
})();

/**
 * Product badge overflow trimming
 *
 * Badges render in priority order: price grade → size → groupset → year.
 * If they don't all fit the container, lowest-priority badges are hidden.
 * Runs on load, window resize, and after AJAX filter updates.
 */
(function () {
  'use strict';

  const GAP = 5; // matches .product-badges { gap: 5px }

  function trimBadges(container) {
    const badges = Array.from(container.children);
    if (!badges.length) return;

    badges.forEach(b => { b.hidden = false; });

    const containerWidth = container.offsetWidth;
    let usedWidth = 0;
    let stop = false;

    badges.forEach((badge, i) => {
      if (stop) {
        badge.hidden = true;
        return;
      }
      const needed = badge.offsetWidth + (i === 0 ? 0 : GAP);
      if (usedWidth + needed <= containerWidth) {
        usedWidth += needed;
      } else {
        badge.hidden = true;
        stop = true;
      }
    });
  }

  function trimAllBadges() {
    document.querySelectorAll('.product-badges').forEach(trimBadges);
  }

  let resizeTimer;

  function init() {
    trimAllBadges();

    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(trimAllBadges, 100);
    }, { passive: true });

    const grid = document.getElementById('FilterProductGrid');
    if (grid) {
      new MutationObserver(() => requestAnimationFrame(trimAllBadges))
        .observe(grid, { childList: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/**
 * Null-guard: Button.attributeChangedCallback (theme.js:87-136)
 *
 * this.target is a getter: document.getElementById(aria-controls).
 * Crashes when the target element is not in the DOM yet or aria-controls
 * references a non-existent id.
 */
(function () {
  'use strict';

  Button.prototype.attributeChangedCallback = function (name, oldValue, newValue) {
    if (!this.target) return;
    if (oldValue === 'false' && newValue === 'true') {
      this.target.open = true;
    } else if (oldValue === 'true' && newValue === 'false') {
      this.target.open = false;
    }
  };
})();

/**
 * Null-guard: loess-scroller-tabs.onButtonClick & updateButtons (theme.js:2976-3027)
 *
 * this.selectedButton returns undefined when no button has aria-expanded="true".
 * onButtonClick then crashes reading .getAttribute on undefined.
 * updateButtons crashes reading .parentElement on undefined.
 */
(function () {
  'use strict';

  var ScrollerTabsClass = customElements.get('loess-scroller-tabs');
  if (!ScrollerTabsClass) return;

  ScrollerTabsClass.prototype.onButtonClick = async function (event) {
    if (!this.selectedButton) return;
    if (this.selectedButton === event.target) return;
    this.contentToHide = document.getElementById(this.selectedButton.getAttribute('aria-controls'));
    this.contentToShow = document.getElementById(event.target.getAttribute('aria-controls'));
    this.updateButtons(event.target);
    if (Shopify.designMode && event.detail && event.detail.load) {
      if (this.contentToHide) this.contentToHide.hidden = true;
      if (this.contentToShow) this.contentToShow.hidden = false;
    } else {
      this.animateContent();
    }
  };

  ScrollerTabsClass.prototype.updateButtons = function (button) {
    if (!this.selectedButton) return;
    this.selectedButton.parentElement.removeAttribute('active');
    button.parentElement.setAttribute('active', '');
    this.selectedButton.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-expanded', 'true');
    this.scrollTo({
      behavior: 'smooth',
      left: this.selectedButton.offsetLeft - this.clientWidth / 2 + this.selectedButton.clientWidth / 2
    });
  };
})();

/**
 * Null-guard: ExpandableHTMLElement focus-trap activation (theme.js:1716-1766)
 *
 * focus-trap throws when a modal/drawer opens with no tabbable child nodes.
 * Adding fallbackFocus to the element itself prevents the crash.
 */
(function () {
  'use strict';

  var originalGetter = Object.getOwnPropertyDescriptor(ExpandableHTMLElement.prototype, 'focusTrapOptions');
  if (!originalGetter || !originalGetter.get) return;

  Object.defineProperty(ExpandableHTMLElement.prototype, 'focusTrapOptions', {
    get: function () {
      return {
        allowOutsideClick: function (event) {
          return event.target.getAttribute('aria-controls') === this.id;
        }.bind(this),
        clickOutsideDeactivates: function (event) {
          return !(event.target.getAttribute('aria-controls') === this.id);
        }.bind(this),
        onDeactivate: function () { this.open = false; }.bind(this),
        fallbackFocus: this,
        ...this._focusTrapOptions
      };
    },
    set: function (options) {
      this._focusTrapOptions = options;
    },
    configurable: true
  });
})();

/**
 * Bridge Shopify's Customer Privacy API (the actual cookie banner, driven by
 * loess-cookie-banner via Shopify.customerPrivacy.setTrackingConsent) to
 * Google Consent Mode. gcm-integration-script.liquid sets the initial
 * "denied" default but only ever re-evaluates it from localStorage.gdprCache,
 * a key nothing in this theme writes (leftover from a since-removed
 * Consentmo app integration) - so ad_storage/ad_user_data/ad_personalization
 * stayed permanently denied regardless of what the visitor chose.
 */
(function () {
  'use strict';

  var latestConsent = null;

  function isGranted(value) {
    return value === true || value === 'yes';
  }

  function syncConsent(source) {
    if (!source) return;
    latestConsent = source;
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }

    var marketing = isGranted(source.marketing);
    var analytics = isGranted(source.analytics);
    var preferences = isGranted(source.preferences);

    gtag('consent', 'update', {
      ad_storage: marketing ? 'granted' : 'denied',
      ad_user_data: marketing ? 'granted' : 'denied',
      ad_personalization: marketing ? 'granted' : 'denied',
      analytics_storage: analytics ? 'granted' : 'denied',
      functionality_storage: preferences ? 'granted' : 'denied',
      personalization_storage: preferences ? 'granted' : 'denied',
      security_storage: 'granted'
    });
  }

  /**
   * Marketing consent as a tri-state, for form submissions that hand it to the
   * edge functions: they only send hashed customer identifiers to an ad
   * platform on an explicit 'granted', and treat 'unknown' like 'denied'.
   */
  window.mnbGetConsentState = function () {
    var source = latestConsent;
    try {
      if (window.Shopify && window.Shopify.customerPrivacy &&
        typeof window.Shopify.customerPrivacy.currentVisitorConsent === 'function') {
        source = window.Shopify.customerPrivacy.currentVisitorConsent() || latestConsent;
      }
    } catch (e) {}
    if (!source) return 'unknown';
    if (isGranted(source.marketing)) return 'granted';
    if (source.marketing === false || source.marketing === 'no') return 'denied';
    return 'unknown';
  };

  document.addEventListener('visitorConsentCollected', function (event) {
    syncConsent(event.detail);
  });

  if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') return;

  window.Shopify.loadFeatures([{
    name: 'consent-tracking-api',
    version: '0.1',
    onLoad: function (error) {
      if (error || !window.Shopify.customerPrivacy) return;
      syncConsent(window.Shopify.customerPrivacy.currentVisitorConsent());
    }
  }]);
})();
